import { useState, useRef, useEffect, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X, Loader2, Plus } from 'lucide-react';

export interface Option {
  value: string;
  label: string;
  /** Optional secondary line rendered under the label (e.g. employee code / department). */
  description?: string;
  /** Optional extra search terms that are matched but not displayed. */
  keywords?: string[];
}

interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  label?: React.ReactNode;
  error?: React.ReactNode;
  inputClassName?: string;
  labelClassName?: string;
  id?: string;
  onQueryChange?: (query: string) => void;
  onAddNew?: (query: string) => void;
  addNewLabel?: string | ((query: string) => string);
  /** Hide the "add new" row while the search box is empty (creatable fields only). */
  hideAddNewWhenEmpty?: boolean;
}

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Search...',
  disabled = false,
  loading = false,
  label,
  error,
  inputClassName = '',
  labelClassName = '',
  id,
  onQueryChange,
  onAddNew,
  addNewLabel,
  hideAddNewWhenEmpty = false,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const selectedLabel = useMemo(() => {
    const opt = options.find((o) => o.value === value);
    return opt?.label ?? '';
  }, [options, value]);

  const filtered = useMemo(() => {
    const q = query.trim();
    if (!q) return options;

    const qLower = q.toLowerCase();
    const qDigits = q.replace(/\D/g, '');
    const stripCountryCode = (value: string) => value.replace(/^(880|88)/, '').replace(/^0/, '');

    return options.filter((o) => {
      const label = o.label.toLowerCase();
      const secondary = `${o.description || ''} ${(o.keywords || []).join(' ')}`.toLowerCase();
      if (label.includes(qLower) || secondary.includes(qLower)) return true;

      // Phone-number tolerant match: ignore +, spaces, dashes and
      // country-code / trunk-zero differences (e.g. 018… vs +88018…).
      if (qDigits.length >= 2) {
        const labelDigits = o.label.replace(/\D/g, '');
        if (labelDigits.includes(qDigits)) return true;
        if (stripCountryCode(labelDigits).includes(stripCountryCode(qDigits))) return true;
      }

      return false;
    });
  }, [options, query]);

  // Rows reachable with the keyboard: the filtered options plus the "add new"
  // row when the consumer provides an `onAddNew` handler.
  const showAddNew = !!onAddNew && (!hideAddNewWhenEmpty || query.trim().length > 0);
  const itemCount = filtered.length + (showAddNew ? 1 : 0);

  // Reset the highlight whenever the list of matches changes.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [query, filtered.length]);

  // Keep the highlighted row scrolled into view while arrowing through.
  useEffect(() => {
    if (!open) return;
    const node = listRef.current?.querySelector<HTMLElement>(`[data-nav-index="${highlightedIndex}"]`);
    node?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex, open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlightedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [open]);

  const updatePosition = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    });
  };

  useLayoutEffect(() => {
    if (open) {
      updatePosition();
      const handleResize = () => updatePosition();
      const handleScroll = () => updatePosition();
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true);
      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [open]);

  const handleSelect = (val: string) => {
    onChange(val);
    setOpen(false);
    setQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
  };

  /** Commit the currently highlighted row (option or the "add new" action). */
  const selectHighlighted = () => {
    if (highlightedIndex < filtered.length) {
      const opt = filtered[highlightedIndex];
      if (opt) handleSelect(opt.value);
      return;
    }
    if (showAddNew && onAddNew) {
      onAddNew(query);
      setOpen(false);
      setQuery('');
    }
  };

  /** Arrow-key navigation / Enter-to-select for the search input. */
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((i) => Math.min(i + 1, Math.max(0, itemCount - 1)));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Home':
        e.preventDefault();
        setHighlightedIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setHighlightedIndex(Math.max(0, itemCount - 1));
        break;
      case 'Enter':
        e.preventDefault();
        selectHighlighted();
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        break;
    }
  };

  const dropdown = open ? (
    <div
      style={dropdownStyle}
      className="bg-surface border border-border rounded-lg shadow-lg max-h-72 flex flex-col overflow-hidden"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="p-2 border-b border-border">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onQueryChange?.(e.target.value);
          }}
          placeholder="Type to search..."
          className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary"
          onKeyDown={handleInputKeyDown}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      <div ref={listRef} className="overflow-y-auto flex-1 p-1">
        {filtered.length === 0 ? (
          <div className="px-3 py-2 text-sm text-text-muted">No results found</div>
        ) : (
          filtered.map((opt, idx) => (
            <button
              key={opt.value}
              type="button"
              data-nav-index={idx}
              onMouseEnter={() => setHighlightedIndex(idx)}
              onClick={() => handleSelect(opt.value)}
              className={`w-full text-left px-3 py-2 rounded transition-colors ${
                opt.value === value
                  ? 'bg-primary/10 text-primary font-medium'
                  : idx === highlightedIndex
                    ? 'bg-background text-text'
                    : 'text-text hover:bg-background'
              }`}
            >
              <span className="block truncate text-sm">{opt.label}</span>
              {opt.description && (
                <span
                  className={`block truncate text-xs mt-0.5 ${
                    opt.value === value ? 'text-primary/80' : 'text-text-muted'
                  }`}
                >
                  {opt.description}
                </span>
              )}
            </button>
          ))
        )}
      </div>
      {showAddNew && (
        <div className="p-2 border-t border-border">
          <button
            type="button"
            data-nav-index={filtered.length}
            onMouseEnter={() => setHighlightedIndex(filtered.length)}
            onClick={() => {
              onAddNew!(query);
              setOpen(false);
              setQuery('');
            }}
            className={`w-full text-left px-3 py-2 text-sm rounded transition-colors text-primary font-medium flex items-center gap-2 ${
              highlightedIndex === filtered.length ? 'bg-primary/10' : 'hover:bg-primary/10'
            }`}
          >
            <Plus size={14} />
            {typeof addNewLabel === 'function'
              ? addNewLabel(query)
              : addNewLabel || (query.trim() ? `Add "${query}"` : 'Add new')}
          </button>
        </div>
      )}
    </div>
  ) : null;

  return (
    <div ref={containerRef} className="relative w-full">
      {label && <label className={labelClassName}>{label}</label>}
      <button
        type="button"
        id={id}
        disabled={disabled || loading}
        onClick={() => !disabled && !loading && setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (!disabled && !loading && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={`w-full flex items-center justify-between px-3 py-2 border rounded-lg bg-background text-sm transition-colors focus:outline-none focus:ring-2 ${
          disabled || loading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
        } ${error ? 'border-danger focus:ring-danger' : 'border-border focus:ring-primary'} ${inputClassName}`}
      >
        <span className={`truncate min-w-0 ${selectedLabel ? 'text-text' : 'text-text-muted'}`}>
          {selectedLabel || placeholder}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {loading && <Loader2 size={14} className="animate-spin text-text-muted" />}
          {value && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={(e) => e.key === 'Enter' && handleClear(e as any)}
              className="p-0.5 rounded hover:bg-surface text-text-muted"
            >
              <X size={14} />
            </span>
          )}
          <ChevronDown size={14} className="text-text-muted" />
        </div>
      </button>

      {dropdown && createPortal(dropdown, document.body)}
      {error}
    </div>
  );
}
